import { useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Card } from "./Card";
import { Loader } from "./Loader";
import { Stack } from "./Stack";
import { Text } from "./Text";
import { ThemeIcon } from "./ThemeIcon";

export interface FileDropzoneProps {
  titulo?: string;
  descripcion?: ReactNode;
  accept: string;
  ctaTexto: string;
  hint?: string;
  cargando?: boolean;
  /** Componente de ícono Tabler, ej. `IconArchive`. */
  Icon: (props: { size?: number; stroke?: number }) => ReactNode;
  onFile: (file: File) => void;
}

/**
 * Reemplazo de `shared/ui/FileDropzone.tsx` · dropzone de UN solo archivo
 * (drag&drop + click-to-select). No es un wrapper de una lib de dropzone:
 * el original tampoco lo era (no usaba el dropzone de Mantine), es un `<div>` a
 * mano con `onDragOver/onDragLeave/onDrop` + `<input type="file">` oculto
 * (visually-hidden, no `display:none`, para que `.click()` programático
 * abra el picker en todos los navegadores — regla de `AGENTS.md`).
 *
 * Los otros dos dropzones del portal (`CFDIPdfDropzone` — dual XML+PDF con
 * slots, `CategoriaDropzone` — multi-archivo con 3 estados y animación)
 * son bespoke y NO usan este átomo · se portan aparte con su pantalla.
 */
export function FileDropzone({ titulo, descripcion, accept, ctaTexto, hint, cargando, Icon, onFile }: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) onFile(f);
    e.target.value = ""; // permite re-seleccionar el mismo archivo
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  }

  return (
    <Card padding="lg">
      <Stack gap="md">
        {(titulo || descripcion) && (
          <div>
            {titulo && (
              <p className="text-base font-semibold text-foreground">{titulo}</p>
            )}
            {descripcion && (
              <p className="mt-1 text-sm text-muted-foreground">{descripcion}</p>
            )}
          </div>
        )}

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !cargando && inputRef.current?.click()}
          className={cn(
            "relative flex flex-col items-center justify-center gap-3 rounded-[10px] border-2 border-dashed p-12 transition-colors",
            cargando ? "cursor-default" : "cursor-pointer",
            dragOver ? "border-multiva-500 bg-multiva-50 dark:bg-multiva-900/20" : "border-border bg-background"
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={onChange}
            className="absolute h-px w-px opacity-0"
            style={{ pointerEvents: "none" }}
          />
          <ThemeIcon size="xl" radius="xl" variant="filled" color="multiva">
            {cargando ? <Loader size="sm" /> : <Icon size={28} stroke={1.6} />}
          </ThemeIcon>
          <Text fw={600} size="sm">
            {ctaTexto}
          </Text>
          {hint && (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          )}
        </div>
      </Stack>
    </Card>
  );
}
